import { Session } from "@supabase/supabase-js";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import z from "zod";

import { supabase } from "@/data/supabase";

const orgTypeSchema = z.enum(["Broker", "Shipper", "Receiver"]);
const appMetaDataSchema = z.object({
  org_id: z.string(),
  org_type: orgTypeSchema,
});

export type AppMetaData = z.infer<typeof appMetaDataSchema>;
export type OrgType = z.infer<typeof orgTypeSchema>;

export type AuthContextType = {
  id: string;
  orgId: string;
  orgType: OrgType;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

type AuthServiceContextType = {
  authState: AuthContextType | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthServiceContext = createContext<AuthServiceContextType>({
  authState: null,
  login: async () => {},
  logout: async () => {},
});

export const useAuthService = () => {
  const context = useContext(AuthServiceContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
    });

    return subscription.unsubscribe;
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    console.log(email, password)
    if (error) {
      console.log("Error signing in:", error.message);
    }
  };

  const signOut = async (): Promise<void> => {
    await supabase.auth.signOut();
  };

  console.log("session:", session);

  const metaData = appMetaDataSchema.safeParse(session?.user?.user_metadata);

  console.log(metaData);

  const authValue: AuthContextType | null =
    session && metaData.success
      ? {
          id: session.user.id,
          orgId: metaData.data.org_id,
          orgType: metaData.data.org_type,
        }
      : null;

  console.log("authValue:", authValue);

  const authServiceValue: AuthServiceContextType = {
    authState: authValue,
    login: signIn,
    logout: signOut,
  };

  return (
    <AuthServiceContext.Provider value={authServiceValue}>
      <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
    </AuthServiceContext.Provider>
  );
};
